import { Checkbox } from 'UI';
import { CheckboxVariant } from 'UI/Checkbox/Checkbox';
import { CouponDiscountTypeEnum } from 'api/generated';
import { GreenTag } from 'assets/icons';
import { useAsyncAction } from 'hooks';
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import styled, { css, keyframes } from 'styled-components';
import { formatNumber, getReducedPrice } from 'utils';

export const SubscribeDtoTypeEnum = {
  Bronze: 'bronze',
  Silver: 'silver',
  Gold: 'gold',
} as const;

export type SubscribeDtoType = typeof SubscribeDtoTypeEnum[keyof typeof SubscribeDtoTypeEnum];

interface SuccessCouponProps {
  subscribeType: SubscribeDtoType;
  courseName: string;
  oldAmount?: number;
  amount: number;
  promoCode?: string;
  setClientSecret: Dispatch<SetStateAction<string | undefined>> | undefined;
  setInvoiceId: Dispatch<SetStateAction<string>>;
  setIsLoadingChangeFirstCoupon: Dispatch<SetStateAction<boolean>>;
  discountAmount: number;
  isLoadingPromocode: boolean;
  invoiceId: string;
  setCertificateFinalPrice: Dispatch<SetStateAction<number | false>>;
  discountValue: number;
  removeCoupon: () => void;
  reset: UseFormReset<any>;
  firstMonthDiscountId?: string;
  firstMonthDiscountAmount?: number;
  firstMonthDiscountType?: string;
  firstMonthDiscountPromoCode?: string;
}

const SuccessCoupon: FC<SuccessCouponProps> = ({
  amount,
  discountValue,
  isLoadingPromocode,
  promoCode,
  invoiceId,
  subscribeType,
  setCertificateFinalPrice,
  setClientSecret,
  setInvoiceId,
  setIsLoadingChangeFirstCoupon,
  firstMonthDiscountId,
  firstMonthDiscountAmount: firstMonthDiscountAmountProp,
  firstMonthDiscountType: firstMonthDiscountTypeProp,
  firstMonthDiscountPromoCode: firstMonthDiscountPromoCodeProp,
}) => {
  //@ts-ignore
  const [changeUseFirstCoupon] = useAsyncAction(actions.payments.changeUseFirstPromoCode);
  const dispatch = useAppDispatch();
  const direction = useAppSelector(selectors.direction.selectDirection);
  const activeCourse = useAppSelector(selectors.courses.selectActiveCourse);
  const selectedCourse = useAppSelector(selectors.courses.selectCourse);
  const signUpModalCourse = useAppSelector(selectors.courses.selectPublicCourse);
  const course = signUpModalCourse || selectedCourse || activeCourse;
  const formattedAmount = formatNumber(amount);

  const checkoutSecondaryCtaColor = (course as any)?.checkoutSecondaryCtaColor as string | undefined;

  // Check if a custom background color was passed in the URL
  const urlParams = new URLSearchParams(window.location.search);
  const hasCustomColor = urlParams.has('color');

  const isFirstDiscount = !!(
    firstMonthDiscountId ||
    // @ts-ignore
    course?.prices?.installment?.[subscribeType]?.firstMonthDiscountCouponId
  );

  const firstMonthDiscountAmount = Number(
    firstMonthDiscountAmountProp ??
      // @ts-ignore
      course?.prices?.installment?.[subscribeType]?.firstMonthDiscountCoupon?.discountAmount ??
      0,
  );

  const firstMonthDiscountType =
    firstMonthDiscountTypeProp ??
    // @ts-ignore
    course?.prices?.installment?.[subscribeType]?.firstMonthDiscountCoupon?.discountType;

  const firstMonthDiscountPromoCode =
    firstMonthDiscountPromoCodeProp ??
    // @ts-ignore
    course?.prices?.installment?.[subscribeType]?.firstMonthDiscountCoupon?.promoCode;

  const earlyFollowUpPaymentEnabled =
    // @ts-ignore
    course?.prices?.installment?.[subscribeType]?.earlyFollowUpPaymentEnabled === true;

  const earlyFollowUpPaymentAmount = Number(
    // @ts-ignore
    course?.prices?.installment?.[subscribeType]?.earlyFollowUpPaymentAmount ?? 0,
  );

  const earlyFollowUpPaymentDelayDays = Number(
    // @ts-ignore
    course?.prices?.installment?.[subscribeType]?.earlyFollowUpPaymentDelayDays ?? 0,
  );

  const totalAmount = getReducedPrice(amount - discountValue, 0);
  const perMonth = Math.floor(totalAmount / 12);
  const useFirstMonthDiscount = useAppSelector(selectors.courses.selectUseFirstMonthDiscount);

  const [isFirstMonthDiscount, setIsFirstMonthDiscount] = useState<boolean>(useFirstMonthDiscount);
  const [shouldJiggle, setShouldJiggle] = useState<boolean>(false);
  const hasJiggledRef = React.useRef(false);

  const getPriceWithFirstMonthDiscount = () => {
    if (firstMonthDiscountType === CouponDiscountTypeEnum.Amount) {
      return Math.max(0, Math.floor(perMonth - firstMonthDiscountAmount));
    } else {
      const discount = (perMonth * firstMonthDiscountAmount) / 100;
      return Math.max(0, Math.floor(perMonth - discount));
    }
  };

  const firstMonthDiscountedPayment = getPriceWithFirstMonthDiscount();
  const firstMonthDiscountSavings = Math.max(perMonth - firstMonthDiscountedPayment, 0);
  const totalAmountWithFirstMonthDiscount = totalAmount - firstMonthDiscountSavings;

  const initialAmount = isFirstMonthDiscount ? firstMonthDiscountedPayment : perMonth;

  const isHybridSchedule =
    !!earlyFollowUpPaymentEnabled && earlyFollowUpPaymentAmount > 0 && earlyFollowUpPaymentDelayDays > 0;
  const monthlyAfterEarlyPayment = isHybridSchedule
    ? Math.max(Math.floor((totalAmountWithFirstMonthDiscount - initialAmount - earlyFollowUpPaymentAmount) / 11), 0)
    : perMonth;

  const displayedOrderTotal = isFirstMonthDiscount ? totalAmountWithFirstMonthDiscount : totalAmount;

  useEffect(() => {
    setIsFirstMonthDiscount(useFirstMonthDiscount);
  }, [useFirstMonthDiscount]);

  // Update the certificate final price when initialAmount changes
  useEffect(() => {
    setCertificateFinalPrice(initialAmount);
  }, [initialAmount, setCertificateFinalPrice]);

  const savingsAmount = firstMonthDiscountSavings;

  const checkboxLabel = isFirstMonthDiscount
    ? `Coupon activated – you saved $${formatNumber(savingsAmount)}`
    : `Tap to activate $${formatNumber(firstMonthDiscountedPayment)} start`;

  useEffect(() => {
    const isPricingLoaded =
      Number.isFinite(amount) &&
      amount > 0 &&
      Number.isFinite(perMonth) &&
      perMonth > 0 &&
      Number.isFinite(firstMonthDiscountAmount);

    if (!isFirstDiscount || isLoadingPromocode || !isPricingLoaded) return;
    if (hasJiggledRef.current) return;

    const startTimeout = window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        setShouldJiggle(true);
        hasJiggledRef.current = true;
      });
    }, 300);

    const stopTimeout = window.setTimeout(() => {
      setShouldJiggle(false);
    }, 300 + 450);

    return () => {
      window.clearTimeout(startTimeout);
      window.clearTimeout(stopTimeout);
    };
  }, [amount, perMonth, firstMonthDiscountAmount, isFirstDiscount, isLoadingPromocode]);

  const toggleIsFirstMonthDiscount = async () => {
    if (!setClientSecret || !direction) return;

    setIsLoadingChangeFirstCoupon(true);
    dispatch(actions.courses.setUseFirstMonthDiscount(!isFirstMonthDiscount));
    setIsFirstMonthDiscount(!isFirstMonthDiscount);

    // Only call API if invoiceId exists (payment intent already created)
    if (invoiceId) {
      const { clientSecret, invoiceId: newInvoiceId } = await changeUseFirstCoupon({
        promocode: promoCode,
        invoiceId,
        useFirstMonthDiscount: !isFirstMonthDiscount,
        directionId: direction?.id,
      });
      setClientSecret(clientSecret);
      setInvoiceId(newInvoiceId);
    } else {
      console.log('First month discount toggled locally - will be applied when payment intent is created');
    }

    setIsLoadingChangeFirstCoupon(false);
  };

  return (
    <div>
      {isFirstDiscount && !isLoadingPromocode && (
        <CouponSection>
          <StyledCheckbox
            variant={CheckboxVariant.default}
            label={checkboxLabel}
            onChange={toggleIsFirstMonthDiscount}
            checked={isFirstMonthDiscount}
            $hasCustomColor={hasCustomColor}
            $shouldJiggle={shouldJiggle}
            $secondaryCtaColor={checkoutSecondaryCtaColor}
          />
        </CouponSection>
      )}

      <SuccessCouponContainer>
        <CouponWrapper>
          <TextCoupon>Product Total:</TextCoupon>
          <TextCoupon2>{`$${formattedAmount}`}</TextCoupon2>
        </CouponWrapper>

        {/* A row saying "undefined: -$0" is worse than no row: guard against the stringified
            undefined that can leak into coupon state, and against zero-value discounts. */}
        {promoCode && promoCode !== 'undefined' && Number(discountValue) > 0 && (
          <DiscountsCouponWrapper isDiscount={!!promoCode}>
            <IconWrapper>
              <GreenTag width={20} height={20} />
            </IconWrapper>
            <TextCoupon>{`${promoCode}:`}</TextCoupon>
            <TextCoupon2>{`-$${discountValue}`}</TextCoupon2>
          </DiscountsCouponWrapper>
        )}

        {/* Gate on the coupon EXISTING, not just on the toggle: a course with no first-month
            coupon (Elite/Master) otherwise template-stringifies undefined into "undefined: -$0". */}
        {isFirstMonthDiscount && firstMonthDiscountPromoCode && Number(savingsAmount) > 0 && (
          <DiscountsCouponWrapper isDiscount={true}>
            <IconWrapper>
              <GreenTag width={20} height={20} />
            </IconWrapper>
            <TextCoupon>{`${firstMonthDiscountPromoCode}:`}</TextCoupon>
            <TextCoupon2>{`-$${formatNumber(savingsAmount)}`}</TextCoupon2>
          </DiscountsCouponWrapper>
        )}

        <OrderCouponWrapper>
          <TextCouponTitleTotal>{'Order Total:'}</TextCouponTitleTotal>
          <TextCouponTotal>{`$${formatNumber(displayedOrderTotal)}`}</TextCouponTotal>
        </OrderCouponWrapper>

        <CouponWrapper>
          <TextCoupon>Initial Payment:</TextCoupon>
          <TextCoupon2>{`$${initialAmount}`}</TextCoupon2>
        </CouponWrapper>

        {isHybridSchedule && (
          <CouponWrapper>
            <TextCoupon>{`Payment In ${earlyFollowUpPaymentDelayDays} Days:`}</TextCoupon>
            <TextCoupon2>{`$${formatNumber(earlyFollowUpPaymentAmount)}`}</TextCoupon2>
          </CouponWrapper>
        )}

        <CouponWrapper>
          <TextCoupon>+11 Payments Of:</TextCoupon>
          <TextCoupon2>{`$${formatNumber(monthlyAfterEarlyPayment)}`}</TextCoupon2>
        </CouponWrapper>
      </SuccessCouponContainer>
    </div>
  );
};

export default SuccessCoupon;

const SuccessCouponContainer = styled.div`
  width: 337px;
  display: grid;
  gap: 7px;
  margin-bottom: 10px;
  border-radius: 8px;
  padding: 12px;
  background-color: ${({ theme: { colors } }) => colors.neutrals[11]};
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
`;

const TextCoupon = styled.span`
  ${({ theme: { typography } }) => css`
    ${typography.body_basic_medium_14}
  `};
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 8px;
`;

const TextCouponTitleTotal = styled.span`
  ${({ theme: { typography } }) => css`
    ${typography.body_basic_bold_14}
  `};
`;

const TextCoupon2 = styled.span`
  margin-left: auto;
  ${({ theme: { typography } }) => css`
    ${typography.body_basic_medium_14}
  `};
`;

const TextCouponTotal = styled.span`
  margin-left: auto;
  ${({ theme: { typography } }) => css`
    ${typography.body_basic_bold_14}
  `};
`;

const couponJiggle = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(2px); }
  50% { transform: translateX(-2px); }
  75% { transform: translateX(1px); }
  100% { transform: translateX(0); }
`;

const isHexColor = (value: string | undefined): value is string => {
  if (!value) return false;
  return /^#([A-Fa-f0-9]{6})$/.test(value);
};

const darkenHex = (hex: string, amount: number) => {
  const safeAmount = Math.min(Math.max(amount, 0), 1);
  const raw = hex.replace('#', '');
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  const darken = (c: number) => Math.max(0, Math.round(c * (1 - safeAmount)));
  const toHex = (c: number) => c.toString(16).padStart(2, '0');
  return `#${toHex(darken(r))}${toHex(darken(g))}${toHex(darken(b))}`;
};

const rgbaFromHex = (hex: string, alpha: number) => {
  const raw = hex.replace('#', '');
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Prevent transient styling props from leaking into Checkbox -> input props.
const CouponCheckboxBase = ({ $hasCustomColor: _a, $shouldJiggle: _b, $secondaryCtaColor: _c, ...props }: any) => (
  <Checkbox {...props} />
);

const StyledCheckbox = styled(CouponCheckboxBase)<{
  $hasCustomColor?: boolean;
  $shouldJiggle?: boolean;
  $secondaryCtaColor?: string;
}>`
  ${({ theme: { typography, colors: _colors }, $hasCustomColor, $shouldJiggle, $secondaryCtaColor }) => {
    const accentColor = '#ee6405';
    const defaultColor = '#22c55e';
    const defaultHoverColor = '#16a34a';

    const hasCourseOverride = isHexColor($secondaryCtaColor);
    const overrideColor = hasCourseOverride ? $secondaryCtaColor : undefined;

    const bgColor = overrideColor ?? ($hasCustomColor ? accentColor : defaultColor);
    const hoverColor = overrideColor ? darkenHex(overrideColor, 0.12) : $hasCustomColor ? '#d55604' : defaultHoverColor;

    const baseShadow = overrideColor
      ? rgbaFromHex(overrideColor, 0.22)
      : $hasCustomColor
      ? 'rgba(238, 100, 5, 0.22)'
      : 'rgba(34, 197, 94, 0.22)';
    const hoverShadow = overrideColor
      ? rgbaFromHex(overrideColor, 0.28)
      : $hasCustomColor
      ? 'rgba(238, 100, 5, 0.28)'
      : 'rgba(34, 197, 94, 0.28)';

    return css`
      /* Keep the ribbon the same visual width as the pricing box (serrated edge stays inside). */
      width: calc(100% - 14px);
      margin-top: 0px;
      margin-bottom: 8px;
      background-color: ${bgColor};
      background-image: none;
      padding: 12px 16px;
      border: none;
      box-shadow: 0 4px 12px ${baseShadow};
      transition: all 0.18s ease;
      position: relative;
      overflow: visible;
      border-radius: 6px;

      ${$shouldJiggle
        ? css`
            animation: ${couponJiggle} 450ms ease-in-out;
          `
        : ''}

      ${typography.body_basic_bold_14}

      /* Bigger serrated edge on the right to appear like a coupon ribbon */
      &::before {
        content: '';
        position: absolute;
        right: -16px;
        top: 0;
        width: 0;
        height: 0;
        border-top: 16px solid ${bgColor};
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-bottom: 16px solid transparent;
        transition: border-top-color 0.18s ease;
        border-width: 22px 18px;
        z-index: 1;
      }

      &::after {
        content: '';
        position: absolute;
        right: -16px;
        bottom: 0;
        width: 0;
        height: 0;
        border-bottom: 16px solid ${bgColor};
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 16px solid transparent;
        transition: border-bottom-color 0.18s ease;
        border-width: 22px 18px;
        z-index: 1;
      }

      /* Checkbox renders a single child wrapper (LabelInput); force text color/stacking */
      & > * {
        color: white;
        position: relative;
        z-index: 2;
      }

      &:hover {
        background-color: ${hoverColor};
        box-shadow: 0 8px 20px ${hoverShadow};
        transform: translateY(-1px);

        &::before {
          border-top-color: ${hoverColor};
        }

        &::after {
          border-bottom-color: ${hoverColor};
        }
      }

      /* Hide the checkbox input completely */
      input[type='checkbox'] {
        display: none !important;
      }

      strong {
        font-weight: 900;
      }
    `;
  }}
`;

const CouponWrapper = styled.div`
  display: flex;
  gap: 40px;
`;

const DiscountsCouponWrapper = styled.div<{ isDiscount: boolean }>`
  display: flex;
  color: ${({ isDiscount }) => (isDiscount ? '#55b47d' : '#9B9B9B')};
`;

const OrderCouponWrapper = styled.div`
  display: flex;
  gap: 40px;
  ${({ theme: { typography } }) => css`
    ${typography.body_basic_bold_14}
  `};
`;

const CouponSection = styled.div`
  width: 337px;
`;
